import _ from 'lodash'
import { withCompPreviewInfo } from '@wix/editor-elements-integrations'
import type { CompDefinition } from '@wix/thunderbolt-becky-types'
import { getTextPresets, ooiStyleParamsUtil, processOoiCss } from '@wix/thunderbolt-commons'
import { isOOI } from './IsOOI'
import { getColorValue, getSiteColors, getTpaFontParamValue, splitPropertiesValues } from './tpaStyleUtils'
import type { WidgetDataViewItem } from './TPAWidget'
import { Experiments, WidgetData } from '@wix/thunderbolt-symbols'

type TPAWidgetDefinition = CompDefinition<never, WidgetDataViewItem, never, never>

const { shouldOverrideStyleParams, overrideStyleParams } = ooiStyleParamsUtil
const isPublicSegment = process.env.PACKAGE_NAME !== 'thunderbolt-ds'

const isCssVarsOnlyWidget = (widgetSpecData: WidgetData[string], experiments: Experiments) =>
	isPublicSegment &&
	!!widgetSpecData.componentFields.isTPACssVars &&
	!!experiments['specs.thunderbolt.ooiCssAsLinkTag']

export const css = withCompPreviewInfo<object, TPAWidgetDefinition>()(
	[
		'compId',
		'compData',
		'styleProperties',
		// @ts-ignore
		'formFactor',
		'language',
		'siteColors',
		'siteFonts',
		'compLayout',
		'hasResponsiveLayout',
		'experiments',
		// @ts-ignore
		'anywhereThemeOverride',
		...(process.env.PACKAGE_NAME === 'thunderbolt-ds'
			? (['componentViewMode', 'isLivePreviewOpenForEditor', 'isAdi'] as const)
			: []),
	],
	({
		compId,
		compData,
		styleProperties,
		// @ts-ignore
		formFactor,
		language,
		siteColors: themeColors,
		siteFonts,
		compLayout,
		hasResponsiveLayout,
		experiments,
		// @ts-ignore
		anywhereThemeOverride,
		componentViewMode,
		// @ts-ignore
		isLivePreviewOpenForEditor,
		// @ts-ignore
		isAdi,
	}) => {
		const shouldRenderAsOoi = isOOI({
			widgetSpec: compData.widgetSpecData,
			componentViewMode,
			isLivePreviewOpenForEditor,
			isAdi,
		})
		if (!shouldRenderAsOoi || !compData.cssConfig) {
			return {}
		}

		const _siteFonts = _.values(siteFonts)
		const {
			color: colorParams,
			number: numberParams,
			boolean: booleanParams,
			font: fontParams,
		} = splitPropertiesValues(styleProperties)

		const styleData = {
			siteColors: getSiteColors(themeColors),
			siteTextPresets: getTextPresets(_siteFonts),
			style: {
				colors: _.mapValues(colorParams, (color, name) =>
					getColorValue(color, styleProperties[`alpha-param_color_${name}`], themeColors)
				),
				numbers: _.mapValues(numberParams, Number),
				booleans: _.mapValues(booleanParams, (booleanAsString) => booleanAsString === 'true'),
				fonts: _.mapValues(fontParams, (fontParam) => getTpaFontParamValue(fontParam, _siteFonts)),
				googleFontsCssUrl: '',
				uploadFontFaces: '',
			},
		}

		const { widgetId, appSettings, tpaData } = compData
		if (appSettings && shouldOverrideStyleParams(widgetId)) {
			styleData.style = overrideStyleParams({
				styleParams: styleData.style,
				widgetId,
				appSettings,
				tpaData,
				isResponsive: hasResponsiveLayout,
				anywhereThemeOverride,
			})
		}

		const dimensions = {
			width: hasResponsiveLayout || compLayout.docked?.left ? ('' as const) : (compLayout.width as number),
			height: hasResponsiveLayout ? ('' as const) : (compLayout.height as number),
		}

		const cssString = processOoiCss({
			styleData,
			prefixSelector: `.${compId}`,
			withStaticCss: true,
			isRTL: language === 'he' || language === 'ar',
			isMobile: formFactor === 'mobile',
			dimensions,
			cssConfig: compData.cssConfig,
			usesCssPerBreakpoint: !!compData.usesCssPerBreakpoint,
			onlyCssVars: isCssVarsOnlyWidget(compData.widgetSpecData, experiments),
			runAndReport: (fn) => fn(), // todo - implement for public
		})

		return cssString
	}
)
